/**
 * Business Value Calculator - Main Container
 * Sprint 6: Journey Calculator Integration
 *
 * 3-Tab Interface:
 * - Tab 1: Benefits (Driver configuration)
 * - Tab 2: Costs (Project costs + duration allocation)
 * - Tab 3: Results (Financial metrics + cash flow chart)
 */

import React, { useState, useEffect } from 'react';
import { CalculatorImportDataV2 } from '../../../server/businessValue/types';

// Import CSS
import '../../styles/business-value-tokens.css';
import '../../styles/business-value-calculator.css';
import '../../styles/driver-card.css';
import '../../styles/accordion.css';

// Tab components
import { BenefitsTab } from './BenefitsTab';
import { CostsTab } from './CostsTab';
import { ResultsTab } from './ResultsTab';

interface BusinessValueCalculatorProps {
  journeyId: string;
  journeyName?: string;
}

type TabId = 'benefits' | 'costs' | 'results';

export const BusinessValueCalculator: React.FC<BusinessValueCalculatorProps> = ({
  journeyId,
  journeyName
}) => {
  // State management
  const [activeTab, setActiveTab] = useState<TabId>('benefits');
  const [calculatorData, setCalculatorData] = useState<CalculatorImportDataV2 | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [totalCosts, setTotalCosts] = useState(0);
  const [totalBenefits, setTotalBenefits] = useState(0);
  const [discountRate] = useState(0.10);
  const [taxRate] = useState(0.25);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Load calculator export data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/journey/${journeyId}/calculator-export`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const { data } = await response.json();
        setCalculatorData(data);
      } catch (err: unknown) {
        console.error('[BusinessValueCalculator] Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    if (journeyId) {
      loadData();
      // Restore from LocalStorage if available
      const saved = localStorage.getItem(`bv-calculator-${journeyId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setTotalCosts(parsed.totalCosts || 0);
          setTotalBenefits(parsed.totalBenefits || 0);
          setLastSaved(new Date(parsed.lastSaved));
        } catch (err) {
          console.warn('[BusinessValueCalculator] Failed to restore from LocalStorage:', err);
        }
      }
    }
  }, [journeyId]);

  // Auto-save to LocalStorage every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (journeyId && (totalCosts > 0 || totalBenefits > 0)) {
        const saveData = {
          totalCosts,
          totalBenefits,
          discountRate,
          taxRate,
          lastSaved: new Date().toISOString()
        };
        localStorage.setItem(`bv-calculator-${journeyId}`, JSON.stringify(saveData));
        setLastSaved(new Date());
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [journeyId, totalCosts, totalBenefits, discountRate, taxRate]);

  // Keyboard navigation for tabs
  const handleTabKeyDown = (e: React.KeyboardEvent, tab: TabId) => {
    const tabs: TabId[] = ['benefits', 'costs', 'results'];
    const currentIndex = tabs.indexOf(tab);

    if (e.key === 'ArrowLeft' && currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
      e.preventDefault();
    }
  };

  // Driver update handler (for Benefits tab)
  const handleDriverUpdate = (_driverId: string, _updates: Record<string, unknown>) => {
    // TODO: Store driver inputs for calculation phase (Week 4)
  };

  // Reset handler with confirmation
  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const confirmReset = () => {
    // Clear state
    setTotalCosts(0);
    setTotalBenefits(0);
    setLastSaved(null);
    // Clear LocalStorage
    if (journeyId) {
      localStorage.removeItem(`bv-calculator-${journeyId}`);
    }
    setShowResetConfirm(false);
    // Reload page to reset all tab states
    window.location.reload();
  };

  const cancelReset = () => {
    setShowResetConfirm(false);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="business-value-calculator">
        <div className="bv-loading">
          <div className="bv-spinner" role="status" aria-label="Loading calculator data" />
          <p className="bv-loading-text">Loading journey recommendations...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="business-value-calculator">
        <div className="bv-error" role="alert">
          <h3 className="bv-error-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Failed to Load Calculator Data
          </h3>
          <p className="bv-error-message">{error}</p>
          {error.includes('No recommendations') && (
            <p className="bv-error-message">
              Please run station analyses in your journey first to generate recommendations.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render main calculator interface
  return (
    <div className="business-value-calculator">
      {/* Header */}
      <header className="bv-header">
        <div className="bv-header-top">
          <div className="bv-header-content">
            <h1 className="bv-title">Business Value Calculator</h1>
            <p className="bv-subtitle">
              Calculate ROI, NPV, IRR, and payback period for your FAIR implementation
            </p>
          </div>
          <button className="bv-reset-button" onClick={handleReset} title="Reset all inputs to defaults">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 4v6h6M23 20v-6h-6" />
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
            Reset to Defaults
          </button>
        </div>
        {calculatorData && (
          <div className="bv-journey-info">
            <span>Journey:</span>
            <span className="bv-journey-badge">
              {journeyName || calculatorData.journey_name || calculatorData.journey_id}
            </span>
            <span>•</span>
            <span>{calculatorData.driver_benefits.length} value drivers</span>
            <span>•</span>
            <span>
              {calculatorData.driver_benefits.reduce((sum, d) => sum + d.total_recommendations, 0)} recommendations
            </span>
            {lastSaved && (
              <>
                <span>•</span>
                <span className="bv-last-saved" title={lastSaved.toLocaleString()}>
                  Last saved: {(() => {
                    const diffMs = Date.now() - lastSaved.getTime();
                    const diffMins = Math.floor(diffMs / 60000);
                    if (diffMins === 0) return 'just now';
                    if (diffMins === 1) return '1 min ago';
                    if (diffMins < 60) return `${diffMins} mins ago`;
                    const diffHours = Math.floor(diffMins / 60);
                    if (diffHours === 1) return '1 hour ago';
                    return `${diffHours} hours ago`;
                  })()}
                </span>
              </>
            )}
          </div>
        )}
      </header>

      {/* Tab Navigation */}
      <div className="bv-tab-nav" role="tablist" aria-label="Calculator sections">
        <button
          role="tab"
          aria-selected={activeTab === 'benefits'}
          aria-controls="benefits-panel"
          id="benefits-tab"
          className={`bv-tab-button ${activeTab === 'benefits' ? 'active' : ''}`}
          onClick={() => setActiveTab('benefits')}
          onKeyDown={(e) => handleTabKeyDown(e, 'benefits')}
          tabIndex={activeTab === 'benefits' ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
          Benefits
        </button>

        <button
          role="tab"
          aria-selected={activeTab === 'costs'}
          aria-controls="costs-panel"
          id="costs-tab"
          className={`bv-tab-button ${activeTab === 'costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('costs')}
          onKeyDown={(e) => handleTabKeyDown(e, 'costs')}
          tabIndex={activeTab === 'costs' ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Costs
        </button>

        <button
          role="tab"
          aria-selected={activeTab === 'results'}
          aria-controls="results-panel"
          id="results-tab"
          className={`bv-tab-button ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
          onKeyDown={(e) => handleTabKeyDown(e, 'results')}
          tabIndex={activeTab === 'results' ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
          Results
        </button>
      </div>

      {/* Tab Content */}
      {calculatorData && (
        <>
          {/* Benefits Tab */}
          <div
            role="tabpanel"
            id="benefits-panel"
            aria-labelledby="benefits-tab"
            className={`bv-tab-content ${activeTab === 'benefits' ? 'active' : ''}`}
            hidden={activeTab !== 'benefits'}
          >
            <BenefitsTab
              data={calculatorData}
              onDriverUpdate={handleDriverUpdate}
              onBenefitsUpdate={(total) => setTotalBenefits(total)}
            />
          </div>

          {/* Costs Tab */}
          <div
            role="tabpanel"
            id="costs-panel"
            aria-labelledby="costs-tab"
            className={`bv-tab-content ${activeTab === 'costs' ? 'active' : ''}`}
            hidden={activeTab !== 'costs'}
          >
            <CostsTab onCostsUpdate={(total) => setTotalCosts(total)} />
          </div>

          {/* Results Tab */}
          <div
            role="tabpanel"
            id="results-panel"
            aria-labelledby="results-tab"
            className={`bv-tab-content ${activeTab === 'results' ? 'active' : ''}`}
            hidden={activeTab !== 'results'}
          >
            <ResultsTab
              totalBenefits={totalBenefits}
              totalCosts={totalCosts}
              discountRate={discountRate}
              taxRate={taxRate}
            />
          </div>
        </>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="bv-modal-overlay" onClick={cancelReset}>
          <div className="bv-modal-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="reset-dialog-title" aria-modal="true">
            <div className="bv-modal-header">
              <h2 id="reset-dialog-title" className="bv-modal-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Reset to Defaults
              </h2>
              <button className="bv-modal-close" onClick={cancelReset} aria-label="Close dialog">×</button>
            </div>
            <div className="bv-modal-body">
              <p className="bv-modal-text">
                This will reset all inputs (benefits, costs, and financial parameters) to their default values.
              </p>
              <p className="bv-modal-text">
                <strong>This action cannot be undone.</strong> Any unsaved changes will be lost.
              </p>
            </div>
            <div className="bv-modal-footer">
              <button className="bv-modal-button bv-modal-button-secondary" onClick={cancelReset}>
                Cancel
              </button>
              <button className="bv-modal-button bv-modal-button-danger" onClick={confirmReset} autoFocus>
                Reset All Inputs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
      >
        {activeTab === 'benefits' && 'Benefits tab selected'}
        {activeTab === 'costs' && 'Costs tab selected'}
        {activeTab === 'results' && 'Results tab selected'}
      </div>
    </div>
  );
};

export default BusinessValueCalculator;
